/**
 * REK-1986 (SSO.5): NANP phone helpers for the new phone-login tab.
 *
 * The Clerk instance's SMS allowlist is US + CA only (SSO.0 toll-fraud
 * guard), and both share the +1 country code — so the login input collects a
 * 10-digit national number and composes E.164 as `+1XXXXXXXXXX`.
 */

/** Strip a user-typed phone value down to at most 10 national digits. */
export function stripPhoneDigits(value: string): string {
  let digits = value.replace(/\D/g, '');
  // Tolerate a pasted "+1 (555) 555-0123" / "1-555-555-0123" by dropping the
  // leading country code when 11 digits arrive starting with 1.
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
}

/** Progressive display formatting: 555 → (555) → (555) 123-4567. */
export function formatNanpPhone(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** True when the value holds a complete 10-digit NANP national number. */
export function isCompleteNanpPhone(digits: string): boolean {
  return /^\d{10}$/.test(digits);
}

/** Poll interval while waiting for Braze inbound YES confirmation. */
export const PHONE_VERIFICATION_POLL_INTERVAL_MS = 2000;

/** Stop fast polling after ~3 minutes (90 × 2s) to avoid unbounded profile reads. */
export const PHONE_VERIFICATION_MAX_POLL_ATTEMPTS = 90;

/** User-facing copy: Braze accepts Y or YES for double opt-in. */
export const PHONE_DOI_REPLY_HINT = "Reply Y or YES to the confirmation text.";

/** After the fast poll budget, continue slow polling so a delayed YES still verifies. */
export const PHONE_VERIFICATION_SLOW_POLL_INTERVAL_MS = 30_000;

/**
 * Fallback toast when `register-phone` returns `verification_window_exceeded`
 * without a `message`. Prefer the server `message` (Braze rolling 24h DOI cap).
 */
export const PHONE_VERIFICATION_WINDOW_EXCEEDED_MESSAGE =
  'You have exceeded the verification window. Try again after 24 hours.';

/** Compose the E.164 form for a 10-digit US/CA national number. */
export function toE164NorthAmerica(digits: string): string {
  return `+1${digits}`;
}

/** Shown when `register-phone` reports SMS registration is unavailable. */
export const PHONE_REGISTRATION_UNAVAILABLE_MESSAGE =
  "Phone verification isn't available right now. Please try again later.";

/** Stable code from `register-phone` when another account already holds the E.164 (REK-2301). */
export const PHONE_ALREADY_BOUND_CODE = "phone_already_bound";

/** User-facing copy for cross-account phone conflicts. */
export const PHONE_ALREADY_BOUND_MESSAGE =
  "This phone number is already linked to another account.";

/**
 * Shown when `register-phone` returns `code: 'rate_limited'` (REK-2290 —
 * 3 attempts / hour per user). Distinct from the same-phone force-resend
 * cooldown 429, which has no `rate_limited` code and keeps its server message.
 */
export const PHONE_REGISTRATION_RATE_LIMITED_MESSAGE =
  "Too many phone registration attempts. Try again in an hour.";

/**
 * `register-phone` 503 bodies. Matched by value as well as by status so a proxy
 * that rewrites the status still gets the neutral copy. Compared
 * case-insensitively with collapsed whitespace, so re-punctuating the server
 * copy can't silently reintroduce the leak.
 */
const PHONE_REGISTRATION_UNAVAILABLE_ERRORS = new Set([
  "sms registration is not enabled",
  "sms registration is not configured",
]);

/** Normalize a server message for comparison (never for display). */
function normalizeServerError(message: string): string {
  return message.toLowerCase().replace(/\s+/g, " ").replace(/[.!]+$/, "");
}

/**
 * Read a JSON object body without throwing.
 *
 * A 503 or 502 often comes from a proxy/CDN rather than the function, with an
 * empty or HTML body. `response.json()` would reject there, and the raised
 * SyntaxError would be toasted in place of the neutral copy — the exact UX this
 * mapping exists to prevent. Returns `{}` so the caller can still map by status.
 */
export async function readJsonObject(response: Response): Promise<Record<string, unknown>> {
  try {
    const parsed: unknown = await response.json();
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/**
 * Pick the message to show when `register-phone` rejects a registration.
 *
 * Its 503 bodies name internal configuration — the `sms_first_verification`
 * flag and the `braze_sms_subscription_group_id` app_config row (REK-927) — so
 * they are replaced with neutral copy. Cross-account conflicts (REK-2301) use
 * a stable `code` and user-facing `error` without naming the other account.
 * Per-user rate limits (REK-2290) use `code: 'rate_limited'` — pass that via
 * `code` so we do not collide with the force-resend cooldown 429. Every other
 * status keeps the server's message, which is user-actionable (invalid number,
 * consent required, resend wait).
 */
export function resolvePhoneRegistrationErrorMessage(
  status: number,
  serverError: string | null | undefined,
  fallback: string,
  code?: string | null,
): string {
  if (code === "rate_limited") {
    return PHONE_REGISTRATION_RATE_LIMITED_MESSAGE;
  }
  const trimmed = typeof serverError === "string" ? serverError.trim() : "";
  const normalized = normalizeServerError(trimmed);
  const isPhoneAlreadyBound =
    code === PHONE_ALREADY_BOUND_CODE ||
    normalized === PHONE_ALREADY_BOUND_CODE ||
    normalized === normalizeServerError(PHONE_ALREADY_BOUND_MESSAGE);
  if (isPhoneAlreadyBound) {
    return trimmed && normalized !== PHONE_ALREADY_BOUND_CODE
      ? trimmed
      : PHONE_ALREADY_BOUND_MESSAGE;
  }
  if (status === 503 || PHONE_REGISTRATION_UNAVAILABLE_ERRORS.has(normalized)) {
    return PHONE_REGISTRATION_UNAVAILABLE_MESSAGE;
  }
  return trimmed || fallback;
}

export type RegisterPhoneSuccessFeedback = {
  kind: "success" | "warning" | "info";
  message: string;
};

/**
 * Map a successful `register-phone` JSON body to honest toast copy.
 * Distinguishes new DOI send vs already-pending vs 24h window exceeded vs DOI failure.
 */
export function feedbackForRegisterPhoneSuccess(
  result: Record<string, unknown>,
): RegisterPhoneSuccessFeedback {
  if (result.already_verified === true) {
    return {
      kind: "success",
      message:
        typeof result.message === "string" && result.message.trim()
          ? result.message
          : "This phone number is already verified.",
    };
  }
  if (result.already_pending === true) {
    return {
      kind: "info",
      message:
        typeof result.message === "string" && result.message.trim()
          ? result.message
          : "Confirmation already sent — reply Y or YES. Tap Resend if you need another.",
    };
  }
  if (result.verification_window_exceeded === true) {
    return {
      kind: "warning",
      message:
        typeof result.message === "string" && result.message.trim()
          ? result.message
          : PHONE_VERIFICATION_WINDOW_EXCEEDED_MESSAGE,
    };
  }
  if (result.double_opt_in_sent === false) {
    return {
      kind: "warning",
      message:
        typeof result.message === "string" && result.message.trim()
          ? result.message
          : "Phone saved, but we could not send the confirmation text. Tap Resend to try again.",
    };
  }
  if (result.consent_recorded === false) {
    return {
      kind: "warning",
      message:
        typeof result.message === "string" && result.message.trim()
          ? result.message
          : "Check your phone — reply Y or YES. We could not save your SMS preference; update it in Settings > Privacy.",
    };
  }
  if (result.stamp_recorded === false) {
    return {
      kind: "warning",
      message:
        typeof result.warning === "string" && result.warning.trim()
          ? result.warning
          : "Confirmation SMS sent; retry if you do not receive it within a few minutes.",
    };
  }
  return {
    kind: "success",
    message:
      typeof result.message === "string" && result.message.trim()
        ? result.message
        : "Check your phone — reply Y or YES to confirm.",
  };
}

/**
 * Parse free-form user input to E.164 (+1XXXXXXXXXX) for US/CA NANP.
 * Returns null when incomplete, overlong, or invalid. An optional leading country
 * code `1` (with or without `+`) is normalized; extra digits are never truncated.
 */
export function parseNanpPhoneInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const digitsOnly = trimmed.replace(/\D/g, "");

  if (trimmed.startsWith("+")) {
    if (!digitsOnly.startsWith("1")) return null;
    const national = digitsOnly.slice(1);
    if (national.length !== 10) return null;
    return toE164NorthAmerica(national);
  }

  let national = digitsOnly;
  if (national.length === 11 && national.startsWith("1")) {
    national = national.slice(1);
  }
  if (national.length > 10) return null;
  if (!isCompleteNanpPhone(national)) return null;
  return toE164NorthAmerica(national);
}
